import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import NavDetails from "components/Elements/NavDetails";
import ActionButtonStyle from "styles/application/ActionButtonStyle";
import { useNavigate } from "react-router-dom";
import AccessWrapper from "components/Modules/AccessWrapper";
import { NoReadOnly } from "helpers/constants";
import PrintIcon from "assets/printer.svg";
import CommonDropdown from "components/Elements/CommonDropdown";
import {
	fileDownload,
	getFileNameFromContentDispositonHeader,
} from "helpers/utils";
import {
	getServiceListReport,
	getServiceListExcel,
	getCMDataExport,
} from "services/reports/reports";
import { findWindows } from "windows-iana";
import { showError } from "../../../redux/common/actions";
import { useDispatch } from "react-redux";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import { useFilters } from "components/Elements/Filters";

const AT = ActionButtonStyle();

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	restore: {
		border: "2px solid",
		borderRadius: "100%",
		height: "35px",
		width: "35px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#307ad6",
	},

	buttons: {
		display: "flex",
		marginLeft: "auto",
		[media]: {
			marginLeft: "0px",
			flexDirection: "column",
			marginBottom: "10px",
			gap: "10px",
		},
	},
	wrapper: {
		display: "flex",
		[media]: {
			marginTop: "10px",
			justifyContent: "space-between",
			flexDirection: "column",
		},
	},
	importButton: { background: "#ED8738" },
}));

const importButton = {
	"&.MuiButton-root": {
		backgroundColor: "#ED8738",
	},
};
function Header({
	setOpenAddService,
	setImportCSV,
	setOpenMultipleChnageStatusPopup,
	dataLength,
	MultipleChangeStatusDisabled,
	customCaptions,
	selectedServices,
	searchFilter,
	siteAppID,
}) {
	// Init hooks
	const { classes } = useStyles();
	const navigate = useNavigate();
	const reduxDispatch = useDispatch();
	const { buildUrlParams, filterValues } = useFilters();

	const [isDownloading, setIsDownloading] = useState(false);

	const { position } = sessionStorage.getItem("me")
		? JSON.parse(sessionStorage.getItem("me"))
		: {};
	
	// Use the same timezone format as search for consistency
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	// Get chips from FilterContext (same as ServiceContent.js)
	const chips = filterValues.chips?.value || [];

	// Build payload using the same approach as fetchServiceList for consistency
	const filterParams = buildUrlParams();
	
	// Extract sort parameters from tableSort filter (same as ServiceContent.js)
	const tableSortParams = filterValues.tableSort?.params || { sortField: "scheduledDate", sortOrder: "asc" };
	
	// Extract individual filter values for CSV export (same as search)
	const serviceStatusFilter = filterValues.serviceStatus || {};
	const statusType = serviceStatusFilter.params?.statusType || "";
	const status = serviceStatusFilter.params?.status || "";
	
	const payload = {
		siteAppId: siteAppID,
		searchFilter: searchFilter || "",
		...filterParams,
		// Include sorting parameters
		sortField: tableSortParams.sortField,
		sort: tableSortParams.sortOrder, // Note: API expects 'sort', filter uses 'sortOrder'
		// Include status fields from service status filter
		statusType,
		status,
		// Include chips for intervals and other chip-based filters
		chips,
		timeZone,
	};

	const serviceReports = [
		{
			id: "1",
			name: `${customCaptions?.servicePlural || "Services"} Report`,
			reportType: "SERVICE_REPORT",
		},
		{
			id: "2",
			name: `${customCaptions?.servicePlural || "Services"} Excel`,
			reportType: "SERVICE_EXCEL",
		},
		{
			id: "3",
			name: "CM Data Export",
			reportType: "CM_DATA_EXPORT",
		},
	];

	const downloadReportHandler = async (reportToBeDownloaded, handleClose) => {
		if (Object.keys(reportToBeDownloaded)?.length < 1) {
			return;
		}
		handleClose();
		try {
			setIsDownloading(true);
			let response;

			if (reportToBeDownloaded.reportType === "SERVICE_REPORT") {
				response = await getServiceListReport(payload);
			} else if (reportToBeDownloaded.reportType === "SERVICE_EXCEL") {
				response = await getServiceListExcel(payload);
			} else if (reportToBeDownloaded.reportType === "CM_DATA_EXPORT") {
				response = await getCMDataExport(payload);
			}

			if (response?.status) {
				const fileName = getFileNameFromContentDispositonHeader(response);
				fileDownload(response, fileName);
				setIsDownloading(false);
			} else {
				const res = JSON.parse(await response?.data.text());
				setIsDownloading(false);
				reduxDispatch(
					showError(
						res?.detail || res.errors?.message || "Failed to download report."
					)
				);
			}
		} catch (err) {
			setIsDownloading(false);
			reduxDispatch(showError("Failed to download report."));
		}
	};

	return (
		<div className={"topContainerCustomCaptions"}>
			<NavDetails
				status={false}
				lastSaved={""}
				staticCrumbs={[
					{
						id: 1,
						name: `${customCaptions.servicePlural} (${dataLength})`,
						url: "",
					},
				]}
				hideLastLogin
				hideLastSave
				hideVersion={true}
			/>
			<div className={classes.wrapper}>
				<div className={classes.buttons}>
					<AccessWrapper
						access={position?.serviceAccess}
						accessList={NoReadOnly}
					>
						{MultipleChangeStatusDisabled && selectedServices.length > 0 ? (
							<HtmlTooltip
								title={`The status of your selected ${customCaptions.servicePlural} can not be changed as they are not the same status`}
							>
								<div>
									<AT.GeneralButton
										sx={importButton}
										onClick={() => setOpenMultipleChnageStatusPopup(true)}
										disabled={MultipleChangeStatusDisabled}
									>
										Change Status
									</AT.GeneralButton>
								</div>
							</HtmlTooltip>
						) : (
							<AT.GeneralButton
								sx={importButton}
								onClick={() => setOpenMultipleChnageStatusPopup(true)}
								disabled={MultipleChangeStatusDisabled}
							>
								Change Status
							</AT.GeneralButton>
						)}
					</AccessWrapper>

					<AccessWrapper
						access={position?.serviceAccess}
						accessList={NoReadOnly}
					>
						<AT.GeneralButton
							sx={importButton}
							className={classes.importButton}
							onClick={() => setImportCSV(true)}
						>
							Import from CSV
						</AT.GeneralButton>
					</AccessWrapper>


					<AccessWrapper
						access={position?.serviceAccess}
						accessList={NoReadOnly}
					>
						<AT.GeneralButton onClick={() => setOpenAddService(true)}>
							Add New
						</AT.GeneralButton>
					</AccessWrapper>

					<CommonDropdown
						iconSrc={PrintIcon}
						isDownloading={isDownloading}
						dataSource={serviceReports}
						dynamicAction={downloadReportHandler}
					/>
				</div>
				{/* <div className="restore">
					<Icon className={classes.restore} name="Restore" />
				</div> */}
			</div>
		</div>
	);
}

export default Header;
